// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useEffect, useRef, useState, type ReactNode } from "react"
import { Box, Button, Typography } from "@mui/material"

import { MultiUserSelector } from "@crm-src/components/multi-user-selector"

import { InlineEditField, type InlineEditRenderApi } from "./inline-edit-field"

/** Debounce window for auto-save so a burst of toggles collapses to one write. */
const AUTOSAVE_DEBOUNCE_MS = 500

interface InlineSalespeopleEditorProps {
  /** Current assignees' user ids (from `assigned_salespeople[].user_id`). */
  value: string[]
  canEdit: boolean
  /**
   * Persist the full new set of salesperson user ids. Implementations use the
   * crm-api bulk-assign endpoint (`bulkAssignSalespeople([recordId], ids)`),
   * whose replace-the-set semantics match this "set the assignees to exactly
   * this list" contract.
   */
  onSave: (userIds: string[]) => Promise<unknown>
  /** The read display (badges / "+N more" / "Unassigned") shown when collapsed. */
  children: ReactNode
  ariaLabel?: string
  errorMessage?: string
  variant?: "cell" | "panel"
}

/**
 * The popover body. Lives only while the popover is open (the shell unmounts it
 * on close), so it re-seeds its draft selection from the current value on every
 * open and `useUsers` (inside MultiUserSelector) only fetches when actually
 * editing.
 *
 * **Auto-save:** there is no Save button — every change (add a salesperson,
 * remove one, or clear to *nobody*) persists automatically, debounced so a quick
 * burst collapses to a single write. The popover stays open across saves so you
 * can assign several people; closing flushes any not-yet-saved change. Clearing
 * the list to empty unassigns everyone (the crm-api bulk endpoint treats an
 * empty `salesperson_ids` as "remove all").
 */
function SalespeopleEditorBody({
  initial,
  api,
  onSave,
}: {
  initial: string[]
  api: InlineEditRenderApi
  onSave: (userIds: string[]) => Promise<unknown>
}) {
  const [selected, setSelected] = useState<string[]>(initial)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef(false)
  const latest = useRef<string[]>(initial)

  const flush = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    if (pending.current) {
      pending.current = false
      void api.save(() => onSave(latest.current))
    }
  }

  // Flush any pending change when the popover closes / unmounts so a fast
  // "change then click away" still persists.
  useEffect(() => () => flush(), [])

  const handleChange = (ids: string[]) => {
    setSelected(ids)
    latest.current = ids
    pending.current = true
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      pending.current = false
      void api.save(() => onSave(ids))
    }, AUTOSAVE_DEBOUNCE_MS)
  }

  return (
    <Box sx={{ p: 2, width: 320, maxWidth: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      <MultiUserSelector value={selected} onValueChange={handleChange} label="Assigned Salespeople" />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {api.saving ? "Saving…" : "Changes save automatically"}
        </Typography>
        <Button size="small" color="inherit" onClick={() => { flush(); api.close() }}>
          Done
        </Button>
      </Box>
    </Box>
  )
}

/**
 * Click-to-edit salesperson assignment — the flagship inline editor. Renders the
 * page's existing salesperson display; on click a popover offers the same
 * multi-select used by the bulk/modal flows, with Save / Cancel. Works the same
 * in list rows (assign right from the list) and on detail/overview pages.
 *
 * See docs/crm.md → "Inline editing".
 */
export function InlineSalespeopleEditor({
  value,
  canEdit,
  onSave,
  children,
  ariaLabel = "Edit salesperson",
  errorMessage = "Failed to update salesperson",
  variant = "cell",
}: InlineSalespeopleEditorProps) {
  return (
    <InlineEditField
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      variant={variant}
      errorMessage={errorMessage}
      renderEditor={(api) => <SalespeopleEditorBody initial={value} api={api} onSave={onSave} />}
    >
      {children}
    </InlineEditField>
  )
}

export default InlineSalespeopleEditor
